/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/organizations-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-organizations-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/organizations-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, name
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/organizations-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-organizations-api-client.ts (API client)
 * - client/api/generated_schemas/organizations.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-06-22T23:44:13.677Z
 * Source config: config/entities/organizations-entity-config.ts
 * Public fields: 7/7 total fields
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  OrganizationsClient,
  OrganizationsQueryOptions,
} from "../generated_clients/generated-organizations-api-client.js";
import {
  OrganizationsInsert,
  OrganizationsUpdate,
  OrganizationsFieldConfig,
} from "../../types/generated_schemas/generated-organizations-schema.js";

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/organizations_business_hooks/

// Query keys with enhanced caching strategy
export const organizationsKeys = {
  all: ["organizations"] as const,
  lists: () => [...organizationsKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...organizationsKeys.lists(), { filters }] as const,
  details: () => [...organizationsKeys.all, "detail"] as const,
  detail: (id: string) => [...organizationsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) =>
    [...organizationsKeys.all, "search", query, fields] as const,
  filter: (filters: Record<string, any>) =>
    [...organizationsKeys.all, "filter", filters] as const,
  sort: (sortBy: string, ascending: boolean) =>
    [...organizationsKeys.all, "sort", sortBy, ascending] as const,
  count: (filters?: Record<string, any>) =>
    [...organizationsKeys.all, "count", filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useOrganizationsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new OrganizationsClient();
};

/**
 * Hook to get all organizations with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with organizations data
 */
export const useOrganizationsList = (options?: OrganizationsQueryOptions) => {
  const client = useOrganizationsClient();

  return useQuery({
    queryKey: organizationsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single organization by ID
 *
 * @param id - The organization ID
 * @returns React Query result with single organization data
 */
export const useOrganizations = (id: string) => {
  const client = useOrganizationsClient();

  return useQuery({
    queryKey: organizationsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/organizations
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/organizations_business_hooks/
 */

/**
 * Hook to create a new organization
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, name
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating organization
 */
export const useCreateOrganizations = () => {
  const client = useOrganizationsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OrganizationsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
    },
  });
};

/**
 * Hook to update an existing organization
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating organization
 */
export const useUpdateOrganizations = () => {
  const client = useOrganizationsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OrganizationsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a organization
 *
 * @returns React Query mutation for deleting organization
 */
export const useDeleteOrganizations = () => {
  const client = useOrganizationsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
    },
  });
};

/**
 * Hook to count organizations with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useOrganizationsCount = (
  filter?: Partial<
    Record<(typeof OrganizationsFieldConfig.filterable)[number], any>
  >,
) => {
  const client = useOrganizationsClient();

  return useQuery({
    queryKey: organizationsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useOrganizationsFieldConfig = () => {
  return OrganizationsFieldConfig;
};
